<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  UiAccordion,
  UiAsyncCombobox,
  UiButton,
  UiCheckbox,
  UiChip,
  UiDialog,
  UiDivider,
  UiInputField,
  UiSegmentedControl,
  UiText,
  useApi,
} from '@shopexperts/shared';
import type { ComboboxItemData, ExpertListingQueryParams } from '@shopexperts/shared';
import { useQuery } from '@tanstack/vue-query';
import { badgeToComboboxItem, useBadgeSearch } from '../../composables/useBadgeSearch';
import { useBadgeBySlug } from '../../composables/useBadgeBySlug';
import { useExpertFacets } from '../../composables/useExpertFacets';
import SmartFilterCheckList from './SmartFilterCheckList.vue';
import { useCitiesByIds, useCitySearchAcrossCountries } from '../../composables/useCityLookup';
import {
  countActiveSmartFilters,
  createEmptySmartFilterState,
  parseExpertsSmartFilterValue,
  serializeExpertsSmartFilterValue,
  type ExpertsSmartFilterState,
} from '../../utils/expertsSmartFilter';
import { normalizeSuperCollectionPreset } from '../../utils/superCollectionPreset';

const props = defineProps<{
  /** Current stored preset query string. */
  value: string;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  (event: 'apply', value: string): void;
}>();

const api = useApi();
const {
  isLoading: isFacetsLoading,
  expertTypeOptions,
  serviceGroups,
  appsAndThemesOptions,
  countryOptions,
  cityOptions,
  languageOptions,
  partnerTierOptions,
} = useExpertFacets();

const state = ref<ExpertsSmartFilterState>(parseExpertsSmartFilterValue(props.value));
const importDraft = ref('');
const importError = ref('');

// Re-seed when the dialog is reopened without being remounted.
watch(open, (isOpen) => {
  if (isOpen) {
    state.value = parseExpertsSmartFilterValue(props.value);
    importDraft.value = '';
    importError.value = '';
  }
});

// Selected ids may point at cities with no published expert yet (metro presets);
// the facet does not list those, so they are resolved by id and merged in.
const { options: selectedCityOptions } = useCitiesByIds(computed(() => state.value.cities));
const knownCityOptions = computed(() => {
  const merged = new Map(cityOptions.value.map(option => [option.value, option]));
  for (const option of selectedCityOptions.value) merged.set(option.value, option);
  return [...merged.values()];
});

const visibleCityOptions = computed(() => {
  const selectedCountries = new Set(state.value.countries);
  return selectedCountries.size
    ? knownCityOptions.value.filter(city => selectedCountries.has(city.country))
    : knownCityOptions.value;
});

const citySearchText = ref('');
const citySearchDebounced = ref('');
let citySearchTimer: ReturnType<typeof setTimeout> | undefined;
watch(citySearchText, (value) => {
  clearTimeout(citySearchTimer);
  citySearchTimer = setTimeout(() => { citySearchDebounced.value = value; }, 250);
});
const { options: citySearchResults, isFetching: isSearchingCities } = useCitySearchAcrossCountries(
  computed(() => state.value.countries),
  citySearchDebounced,
);
const citySearchItems = computed<ComboboxItemData[]>(() => citySearchResults.value
  .filter(option => !state.value.cities.includes(option.value))
  .map(option => ({ value: option.value, label: `${option.label} (${option.country})` })));

function addCity(selection: { value: string | string[] }) {
  const id = Array.isArray(selection.value) ? selection.value[0] : selection.value;
  if (id && !state.value.cities.includes(id)) state.value.cities = [...state.value.cities, id];
  citySearchText.value = '';
}

watch(() => [...state.value.countries], (countries) => {
  if (!countries.length || !state.value.cities.length) return;
  const selectedCountries = new Set(countries);
  const countryByCity = new Map(cityOptions.value.map(city => [city.value, city.country]));
  const kept = state.value.cities.filter((id) => {
    const country = countryByCity.get(id);
    return country === undefined || selectedCountries.has(country);
  });
  if (kept.length !== state.value.cities.length) state.value.cities = kept;
});

const activeCount = computed(() => countActiveSmartFilters(state.value));
const serialized = computed(() => serializeExpertsSmartFilterValue(state.value));

const expertTypeSegments = computed(() => [
  { label: 'Any', value: '' },
  ...(expertTypeOptions.value.length
    ? expertTypeOptions.value
    : [
        { label: 'Freelancer', value: 'freelancer' },
        { label: 'Agency', value: 'agency' },
        { label: 'App service provider', value: 'app_service_provider' },
      ]),
]);

const priceError = computed(() => {
  const from = state.value.price_from;
  const to = state.value.price_to;
  if (from && !/^\d+$/.test(from)) return 'Minimum must be a whole number.';
  if (to && !/^\d+$/.test(to)) return 'Maximum must be a whole number.';
  if (from && to && Number(from) > Number(to)) return 'Minimum cannot be greater than maximum.';
  return '';
});

// Live "how many experts match" count, debounced so typing a budget or
// toggling several checkboxes fires one request.
const debouncedQuery = ref('');
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
watch(serialized, (value) => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { debouncedQuery.value = value; }, 400);
}, { immediate: true });

function toListingQuery(current: ExpertsSmartFilterState): ExpertListingQueryParams {
  const query: ExpertListingQueryParams = {};
  if (current.expert_type) query.expert_type = current.expert_type as ExpertListingQueryParams['expert_type'];
  if (current.services_offered.length) query.services_offered = current.services_offered;
  if (current.countries.length) query.countries = current.countries;
  if (current.cities.length) query.cities = current.cities.map(Number);
  if (current.languages.length) query.languages = current.languages;
  if (current.apps_and_themes.length) query.apps_and_themes = current.apps_and_themes;
  if (current.partner_tiers.length) query.partner_tiers = current.partner_tiers as ExpertListingQueryParams['partner_tiers'];
  if (current.partner_badge) query.partner_badge = current.partner_badge;
  if (/^\d+$/.test(current.price_from) || /^\d+$/.test(current.price_to)) {
    query.starting_price = {
      ...(/^\d+$/.test(current.price_from) ? { from: Number(current.price_from) } : {}),
      ...(/^\d+$/.test(current.price_to) ? { to: Number(current.price_to) } : {}),
    };
  }
  if (current.search.trim()) query.search = current.search.trim();
  if (current.is_pro) query.is_pro = true;
  if (current.ranking === 'most_recent') query.ranking = 'most_recent';
  return query;
}

const { data: matchData, isFetching: isCounting } = useQuery({
  queryKey: computed(() => ['admin', 'experts-smart-filter', 'match-count', debouncedQuery.value]),
  queryFn: async () => api.public.expert.getPublicExpertListings({
    query: toListingQuery(parseExpertsSmartFilterValue(debouncedQuery.value)),
    perPage: 1,
  }),
  enabled: open,
  staleTime: 60 * 1000,
});
const matchCount = computed(() => matchData.value?.meta?.count);
const matchCountText = computed(() => {
  if (isCounting.value) return 'Counting matching experts...';
  if (typeof matchCount.value !== 'number') return '';
  if (matchCount.value === 0) return 'No experts match these filters.';
  if (matchCount.value === 1) return '1 expert matches these filters.';
  return `${matchCount.value.toLocaleString()} experts match these filters.`;
});

// Partner badge picker — the preset stores a single badge slug, edited with
// the same async combobox contract as the partner_badge metafield field.
const badgeSearchQuery = ref('');
const badgeSelectedItems = ref<ComboboxItemData[]>([]);
const { comboboxItems: badgeComboboxItems, isLoading: isBadgeSearching } = useBadgeSearch({
  search: badgeSearchQuery,
  status: 'active',
  limit: 20,
});

const { badge: selectedBadge } = useBadgeBySlug(computed(() => state.value.partner_badge));
const selectedBadgeId = computed(() => selectedBadge.value?.id?.toString());
const selectedBadgeName = computed(() => (
  state.value.partner_badge
    ? selectedBadge.value?.name || state.value.partner_badge
    : ''
));

// Resolve the saved slug so an existing selection renders with its label and
// icon, mirroring picking it by hand in preserve mode — but never refill the
// input after the user cleared it to type a new search.
let appliedBadgeLabel: string | null = null;
watch([() => state.value.partner_badge, selectedBadge], () => {
  if (!state.value.partner_badge) {
    badgeSelectedItems.value = [];
    if (appliedBadgeLabel && badgeSearchQuery.value === appliedBadgeLabel) {
      badgeSearchQuery.value = '';
    }
    appliedBadgeLabel = null;
    return;
  }
  const resolved = selectedBadge.value;
  if (!resolved) return;

  badgeSelectedItems.value = [badgeToComboboxItem(resolved)];
  if (resolved.name !== appliedBadgeLabel) {
    if (!badgeSearchQuery.value || badgeSearchQuery.value === appliedBadgeLabel) {
      badgeSearchQuery.value = resolved.name;
    }
    appliedBadgeLabel = resolved.name;
  }
}, { immediate: true });

function onBadgeSelect(event: { value: string | string[]; metadata?: Record<string, unknown> | Record<string, unknown>[] }) {
  const metadata = Array.isArray(event.metadata) ? event.metadata[0] : event.metadata;
  const nextValue = Array.isArray(event.value) ? event.value[0] : event.value;
  const slug = typeof metadata?.slug === 'string' ? metadata.slug : '';
  state.value.partner_badge = nextValue && slug ? slug : '';
}

// Import a filtered /experts/all URL (or raw query) into the structured state.
function importFromUrl() {
  const raw = importDraft.value.trim();
  if (!raw) return;
  const { errors } = normalizeSuperCollectionPreset(raw);
  const blocking = errors.filter(error => !error.startsWith('Choose at least one'));
  if (blocking.length) {
    importError.value = blocking[0] ?? '';
    return;
  }
  state.value = parseExpertsSmartFilterValue(raw);
  importDraft.value = '';
  importError.value = '';
}

function clearAll() {
  state.value = createEmptySmartFilterState();
}

function apply() {
  if (priceError.value) return;
  emit('apply', serialized.value);
  open.value = false;
}

function sectionCount(values: string[]): string {
  return values.length ? String(values.length) : '';
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Expert filters"
    text="Choose which experts this collection shows. Filters combine, so each one narrows the results."
    size="xl"
    primary-action-label="Apply filters"
    secondary-action-label="Cancel"
    hide-scrollbar
    @primary-action="apply"
    @secondary-action="open = false"
  >
    <div class="flex flex-col gap-4">
      <!-- Sticky within UiDialog's scrollable body, so the active-filter
           summary and Clear all stay visible while the sections scroll. -->
      <div class="sticky top-0 z-10 border-b border-se-gray-200 bg-white pb-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <UiChip :text="`${activeCount} active`" size="tag" :variant="activeCount ? 'success' : 'neutral'" />
            <UiText as="p" variant="caption-normal" class="text-se-gray-700" :text="matchCountText" />
          </div>
          <UiButton
            v-if="activeCount"
            label="Clear all"
            variant="ghost"
            size="sm"
            hover-style="highlight"
            @click="clearAll"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <UiText as="p" variant="label" text="Expert type" />
        <UiSegmentedControl v-model="state.expert_type" :options="expertTypeSegments" name="smart-filter-expert-type" />
      </div>

      <UiDivider />

      <UiAccordion v-if="isFacetsLoading || serviceGroups.length || state.services_offered.length" :open="Boolean(state.services_offered.length)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Services" />
            <UiChip v-if="state.services_offered.length" :text="sectionCount(state.services_offered)" size="tag" variant="neutral" />
          </div>
        </template>
        <div class="pt-2 pb-1">
          <SmartFilterCheckList
            v-model="state.services_offered"
            :groups="serviceGroups"
            :loading="isFacetsLoading"
            searchable
            search-placeholder="Search services and subcategories..."
          />
        </div>
      </UiAccordion>

      <UiAccordion v-if="isFacetsLoading || appsAndThemesOptions.length || state.apps_and_themes.length" :open="Boolean(state.apps_and_themes.length)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Apps & themes" />
            <UiChip v-if="state.apps_and_themes.length" :text="sectionCount(state.apps_and_themes)" size="tag" variant="neutral" />
          </div>
        </template>
        <div class="pt-2 pb-1">
          <SmartFilterCheckList
            v-model="state.apps_and_themes"
            :options="appsAndThemesOptions"
            :loading="isFacetsLoading"
            searchable
            search-placeholder="Search apps and themes..."
          />
        </div>
      </UiAccordion>

      <UiAccordion v-if="isFacetsLoading || countryOptions.length || state.countries.length" :open="Boolean(state.countries.length)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Location" />
            <UiChip v-if="state.countries.length" :text="sectionCount(state.countries)" size="tag" variant="neutral" />
          </div>
        </template>
        <div class="pt-2 pb-1">
          <SmartFilterCheckList
            v-model="state.countries"
            :options="countryOptions"
            :loading="isFacetsLoading"
            searchable
            search-placeholder="Search countries..."
          />
        </div>
      </UiAccordion>

      <UiAccordion v-if="isFacetsLoading || cityOptions.length || state.cities.length" :open="Boolean(state.cities.length)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="City" />
            <UiChip v-if="state.cities.length" :text="sectionCount(state.cities)" size="tag" variant="neutral" />
          </div>
        </template>
        <div class="pt-2 pb-1">
          <SmartFilterCheckList
            v-model="state.cities"
            :options="visibleCityOptions"
            :loading="isFacetsLoading"
            searchable
            search-placeholder="Filter listed cities..."
          />
          <div class="mt-3 flex flex-col gap-1">
            <UiText as="p" variant="caption-normal" class="text-se-gray-500" text="Add any city in the selected locations, including ones with no experts yet." />
            <UiAsyncCombobox
              v-model="citySearchText"
              :data="citySearchItems"
              :is-fetching="isSearchingCities"
              :disabled="!state.countries.length"
              :placeholder="state.countries.length ? 'Search cities to add...' : 'Pick a location first'"
              display-mode="inline"
              mode="clearOnSelect"
              size="md"
              hide-combobox-when-empty
              @select="addCity"
            />
          </div>
        </div>
      </UiAccordion>

      <UiAccordion v-if="isFacetsLoading || languageOptions.length || state.languages.length" :open="Boolean(state.languages.length)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Languages" />
            <UiChip v-if="state.languages.length" :text="sectionCount(state.languages)" size="tag" variant="neutral" />
          </div>
        </template>
        <div class="pt-2 pb-1">
          <SmartFilterCheckList
            v-model="state.languages"
            :options="languageOptions"
            :loading="isFacetsLoading"
            searchable
            search-placeholder="Search languages..."
          />
        </div>
      </UiAccordion>

      <UiAccordion v-if="isFacetsLoading || partnerTierOptions.length || state.partner_tiers.length" :open="Boolean(state.partner_tiers.length)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Shopify partner tier" />
            <UiChip v-if="state.partner_tiers.length" :text="sectionCount(state.partner_tiers)" size="tag" variant="neutral" />
          </div>
        </template>
        <div class="pt-2 pb-1">
          <SmartFilterCheckList
            v-model="state.partner_tiers"
            :options="partnerTierOptions"
            :loading="isFacetsLoading"
          />
        </div>
      </UiAccordion>

      <UiAccordion :open="Boolean(state.partner_badge)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Partner badge" />
            <UiChip v-if="state.partner_badge" :text="selectedBadgeName" size="tag" variant="neutral" />
          </div>
        </template>
        <!-- The shared combobox control diverges from UiFieldContainer: py-2.5
             instead of h-10, a ring (drawn outside the box) instead of a 1px
             border with bg-white, and a w-5 rotating chevron where UiSelect
             uses a static w-4 one. Override all so it matches the kit's input
             chrome exactly. -->
        <div
          class="pt-2 pb-1
            [&_[data-part=control]]:h-10 [&_[data-part=control]]:min-h-10 [&_[data-part=control]]:py-0
            [&_[data-part=control]]:bg-white [&_[data-part=control]]:ring-0
            [&_[data-part=control]]:border [&_[data-part=control]]:border-se-gray-300
            [&_[data-part=control]:hover]:border-se-gray-400
            [&_[data-part=control]:focus-within]:border-se-gray-500
            [&_[data-part=trigger]_svg]:w-4 [&_[data-part=trigger]_svg]:h-4
            [&_[data-part=trigger]]:transform-none"
        >
          <UiAsyncCombobox
            v-model="badgeSearchQuery"
            v-model:selected-items="badgeSelectedItems"
            :selected-id="selectedBadgeId"
            :data="badgeComboboxItems"
            :is-fetching="isBadgeSearching"
            size="md"
            display-mode="combobox"
            :show-selected-image="true"
            mode="preserve"
            placeholder="Search partner badges..."
            @select="onBadgeSelect"
          />
        </div>
      </UiAccordion>

      <UiAccordion :open="Boolean(state.price_from || state.price_to)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Starting project price" />
            <UiChip
              v-if="state.price_from || state.price_to"
              :text="`$${state.price_from || '0'} – ${state.price_to ? `$${state.price_to}` : 'any'}`"
              size="tag"
              variant="primary"
            />
          </div>
        </template>
        <div class="flex flex-col gap-2 pt-2 pb-1">
          <div class="flex items-center gap-3">
            <UiInputField v-model="state.price_from" name="smart-filter-price-from" type="number" placeholder="Min ($)" />
            <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="to" />
            <UiInputField v-model="state.price_to" name="smart-filter-price-to" type="number" placeholder="Max ($)" />
          </div>
          <UiText v-if="priceError" as="p" variant="caption-normal" class="text-se-danger" :text="priceError" />
        </div>
      </UiAccordion>

      <UiAccordion :open="Boolean(state.search || state.is_pro || state.ranking)">
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="More options" />
            <UiChip
              v-if="state.search || state.is_pro || state.ranking"
              :text="String([state.search, state.is_pro, state.ranking].filter(Boolean).length)"
              size="tag"
              variant="primary"
            />
          </div>
        </template>
        <div class="flex flex-col gap-3 pt-2 pb-1">
          <UiInputField
            v-model="state.search"
            name="smart-filter-search"
            icon="search-md"
            placeholder="Keyword search (name, bio, services)"
          />
          <UiCheckbox v-model="state.is_pro" label="Pro experts only" description="Limit results to experts on a Pro plan." />
          <UiCheckbox
            :model-value="state.ranking === 'most_recent'"
            label="Rank by most recent"
            description="Order results by recent activity instead of the default all-time ranking."
            @update:model-value="state.ranking = $event ? 'most_recent' : ''"
          />
        </div>
      </UiAccordion>

      <UiAccordion>
        <template #trigger>
          <div class="flex items-center gap-2 py-1">
            <UiText as="p" variant="label" text="Import from URL" />
          </div>
        </template>
        <div class="flex flex-col gap-2 pt-2 pb-1">
          <UiText
            as="p"
            variant="caption-normal"
            class="text-se-gray-700"
            text="Set up filters on /experts/all, copy the URL, and paste it here to load the same filters."
          />
          <div class="flex items-start gap-2">
            <UiInputField
              v-model="importDraft"
              name="smart-filter-import"
              placeholder="https://shopexperts.com/experts/all?services_offered=seo"
              class="flex-1"
              :invalid="Boolean(importError)"
              :error="importError"
              @update:model-value="importError = ''"
            />
            <UiButton label="Import" variant="outline" size="sm" @click="importFromUrl" />
          </div>
        </div>
      </UiAccordion>
    </div>
  </UiDialog>
</template>
